import React, { useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import * as Yup from "yup";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AsyncContentMui } from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { useDateFormatSelect, useDealActions } from "store";
import {
  CurrencyType,
  ICreateInvestmentDetailsData,
  IInvestmentDetailCellValue,
  IInvestmentDetailColumn,
  IInvestmentDetailValue,
  IUpdateInvestmentDetailHistoryData
} from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../constants";
import { HistoryDataRow } from "./components/HistoryDataRow";

interface InitialValues {
  value: string;
  currency: CurrencyType | null;
  date: string | null;
}

interface EditCellPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  cellData: IInvestmentDetailCellValue;
  columnData: IInvestmentDetailColumn;
  onSubmit: (value: IInvestmentDetailCellValue) => void;
  dealId: number;
}

export const EditCellPopover: React.FC<EditCellPopoverProps> = ({
  anchorEl,
  onClose,
  cellData,
  columnData,
  onSubmit,
  dealId
}) => {
  const dateFormat = useDateFormatSelect();
  const [isAddNewRow, setIsAddNewRow] = useState(false);

  const [updatedHistoryData, setUpdatedHistoryData] = useState<Record<number, IInvestmentDetailValue>>({});
  const [deletedHistoryIds, setDeletedHistoryIds] = useState<number[]>([]);

  const { getInvestmentDetailsHistoryDataAction, saveInvestmentDetailDataAction } = useDealActions();

  const {
    action: getHistoryData,
    loading,
    data: historyData = [],
    setData: setHistoryData
  } = useActionHook(getInvestmentDetailsHistoryDataAction);

  const { action: updateHistoryData } = useActionHook(saveInvestmentDetailDataAction, {
    onSuccess: (data) => {
      onSubmit(data);
      onClose();
    }
  });

  const { column_type, data_type, title, id: investment_detail_id } = columnData;
  const { id, with_history, uuid } = cellData;

  // Update history data function
  const handleHistoryDataUpdate = (updatedItem: IInvestmentDetailValue) => {
    setUpdatedHistoryData((prev) => ({
      ...prev,
      [updatedItem.id]: updatedItem
    }));
  };

  // Function for deleting history data
  const handleHistoryDataDelete = (id: number) => {
    // Add id to deleted array
    setDeletedHistoryIds((prev) => [...prev, id]);

    // Remove item from display
    setHistoryData(historyData.filter((item) => item.id !== id));

    // Remove item from updated array if it exists
    setUpdatedHistoryData((prev) => {
      const newUpdatedHistoryData = { ...prev };
      delete newUpdatedHistoryData[id];
      return newUpdatedHistoryData;
    });
  };

  const initialValues: InitialValues = {
    value: data_type === "string" && cellData.value ? cellData.value : "",
    currency: data_type === "string" ? null : "USD",
    date: null
  };

  let valueSchema: Yup.StringSchema<string | undefined | null> | Yup.NumberSchema<number | undefined | null>;
  if (column_type === "number") {
    if (isAddNewRow) {
      valueSchema = Yup.number().required("Required").min(0, "Value must be greater than 0");
      if (data_type === "percentage") {
        valueSchema = valueSchema.max(100, "Value must be less than 100");
      }
    } else {
      // When editing existing history rows, value is optional
      valueSchema = Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue === null || originalValue === undefined) {
            return null;
          }
          return value;
        })
        .min(0, "Value must be greater than 0");
      if (data_type === "percentage") {
        valueSchema = valueSchema.max(100, "Value must be less than 100");
      }
    }
  } else if (isAddNewRow) {
    valueSchema = Yup.string().required("Required");
  } else {
    valueSchema = Yup.string().nullable();
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      value: valueSchema,
      date:
        with_history && isAddNewRow ? Yup.string().nullable().required("Date is required") : Yup.string().nullable(),
      currency:
        data_type === "currency" && isAddNewRow
          ? Yup.string().required("Currency is required")
          : Yup.string().nullable()
    }),
    onSubmit: (values) => {
      if (!dealId) return;

      const basePayload: IUpdateInvestmentDetailHistoryData = {
        dealId,
        created: [],
        updated: [],
        deleted: deletedHistoryIds
      };

      // Handle text column type
      if (column_type === "text" && id && values.value) {
        updateHistoryData({
          ...basePayload,
          updated: [
            {
              id,
              value: values.value,
              currency: null,
              date: null
            }
          ]
        });
        return;
      }

      // Handle other column types
      const payloadData = {
        ...basePayload,
        updated: Object.values(updatedHistoryData)
      };

      // Add new value if exists
      if (values.value) {
        const newEntry: Omit<ICreateInvestmentDetailsData, "dealId"> = {
          uuid,
          investment_detail_id,
          value: values.value,
          ...(values.currency && data_type === "currency" && { currency: values.currency }),
          ...(values.date && data_type !== "string" && { date: values.date })
        };

        payloadData.created.push(newEntry);
      }

      updateHistoryData(payloadData);
    }
  });

  const onHandleClose = () => {
    formik.resetForm();
    onClose();
  };

  useEffect(() => {
    if (with_history && id && dealId) {
      getHistoryData(dealId, id);
    }
  }, [with_history]);

  const renderContent = useMemo(() => {
    switch (data_type) {
      case "currency":
      case "percentage":
        return (
          <Stack direction="column" gap="15px">
            {isAddNewRow ? (
              <Stack direction="row" alignItems="flex-start" gap="5px" sx={{ width: "100%" }}>
                <Box sx={{ width: "160px", minHeight: "56px" }}>
                  <DesktopDatePicker
                    label="Date"
                    format={dateFormat}
                    views={["year", "month", "day"]}
                    value={formik.values.date ? dayjs(formik.values.date) : null}
                    onChange={(newValue) => {
                      if (newValue && newValue.isValid()) {
                        formik.setFieldValue("date", toIsoMidday(newValue.toISOString()));
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        error: formik.touched.date && Boolean(formik.errors.date),
                        helperText: formik.touched.date && (formik.errors.date as string)
                      }
                    }}
                  />
                </Box>
                <Box sx={{ width: "160px", minHeight: "56px" }}>
                  <NumberFormat
                    thousandSeparator={data_type !== "percentage"}
                    isNumericString
                    variant="outlined"
                    size="small"
                    sx={{ width: "160px" }}
                    decimalScale={2}
                    label="Value"
                    suffix={data_type === "percentage" ? "%" : ""}
                    customInput={TextField}
                    value={formik.values.value}
                    onValueChange={({ value }) => formik.setFieldValue("value", value)}
                    error={formik.touched.value && Boolean(formik.errors.value)}
                    helperText={formik.touched.value && (formik.errors.value as string)}
                  />
                </Box>
                {data_type === "currency" && (
                  <Box sx={{ width: "120px", minHeight: "56px" }}>
                    <TextField
                      select
                      label="Currency"
                      size="small"
                      sx={{ width: "120px", "& .MuiSelect-select": { padding: "8.5px 5px !important" } }}
                      value={formik.values.currency}
                      onChange={(e) => formik.setFieldValue("currency", e.target.value)}
                      error={formik.touched.currency && Boolean(formik.errors.currency)}
                      helperText={formik.touched.currency && (formik.errors.currency as string)}
                    >
                      {currenciesArray.map((currency, index) => (
                        <MenuItem value={currency} key={index}>
                          {currency}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
                <IconButton onClick={() => setIsAddNewRow(false)}>
                  <CloseOutlinedIcon sx={{ fontSize: "16px", cursor: "pointer" }} />
                </IconButton>
              </Stack>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                sx={{ cursor: "pointer", gap: "5px" }}
                onClick={() => setIsAddNewRow(true)}
              >
                <AddCircleOutlineOutlinedIcon sx={{ fill: "#26A6AE" }} />
                <Typography sx={{ color: "#26A6AE" }}>Add Value</Typography>
              </Stack>
            )}
            {!!historyData.length && (
              <TableContainer sx={{ borderRadius: "16px", border: "1px solid #DBE8F5" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F8F9FD" }}>
                    <TableRow>
                      <TableCell>Value</TableCell>
                      {data_type === "currency" && <TableCell>Currency</TableCell>}
                      <TableCell>Date</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.map((item) => (
                      <HistoryDataRow
                        item={item}
                        key={item.id}
                        onUpdate={handleHistoryDataUpdate}
                        onDelete={handleHistoryDataDelete}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        );
      // case "percentage":
      //   return (
      //     <NumberFormat
      //       customInput={TextField}
      //       thousandSeparator
      //       isNumericString
      //       variant="outlined"
      //       size="small"
      //       fullWidth
      //       label={title}
      //       value={formik.values.value}
      //       onValueChange={({ value }) => formik.setFieldValue("value", value)}
      //       suffix={"%"}
      //       error={formik.touched.value && Boolean(formik.errors.value)}
      //       helperText={formik.touched.value && (formik.errors.value as string)}
      //     />
      //   );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={title}
            value={formik.values.value}
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "" && id) {
                setDeletedHistoryIds((prev) => [...prev, id]);
              }
            }}
            name="value"
            error={formik.touched.value && Boolean(formik.errors.value)}
            helperText={formik.touched.value && (formik.errors.value as string)}
          />
        );
    }
  }, [data_type, title, formik.values, formik.touched, formik.errors, historyData, isAddNewRow, dateFormat]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onHandleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left"
      }}
      sx={{
        ".MuiPopover-paper": {
          overflow: "auto",
          borderRadius: "8px",
          minWidth: 335,
          p: "20px"
        }
      }}
    >
      <Box>
        <AsyncContentMui isLoading={loading} spinnerProps={{ size: 100 }}>
          <form onSubmit={formik.handleSubmit}>
            <Stack gap="20px">
              <Stack direction="column" gap="30px">
                <Typography variant="subtitle1">{title}</Typography>
                {renderContent}
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" type="button" onClick={onHandleClose}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" sx={{ bgcolor: "#071F35" }}>
                  Apply
                </Button>
              </Stack>
            </Stack>
          </form>
        </AsyncContentMui>
      </Box>
    </Popover>
  );
};
